import { useRef, type RefObject } from 'react'

import { clsx } from 'clsx'
import { motion } from 'motion/react'

import { IconButton, SmileIcon, Textarea } from '@buffer-mono/popcorn'

import type { SavedReply } from '~publish/gql/graphql'

import { MAX_CHARS } from './ReplyFormFooter'
import {
  EmojiPicker,
  useTextareaEmojiInsert,
} from '../../../components/EmojiPicker'
import { useCommentData } from '../context/CommentContext'
import { SavedRepliesPopover } from '../SavedRepliesPopover'

import styles from './AnimatedTextarea.module.css'

type AnimatedTextareaProps = {
  id?: string
  textareaRef: RefObject<HTMLTextAreaElement>
  replyText: string
  setReplyText: (text: string) => void
  isExpanded: boolean
  isLoading: boolean
  error?: string
  handleExpand: () => void
  handleFocus: () => void
  savedRepliesPopoverRef: RefObject<HTMLDivElement>
  handleInsertSavedReply: (savedReply: SavedReply) => void
  emojiPickerRef: RefObject<HTMLDivElement>
}

export const AnimatedTextarea = ({
  id,
  textareaRef,
  replyText,
  setReplyText,
  isExpanded,
  isLoading,
  error,
  handleExpand,
  handleFocus,
  savedRepliesPopoverRef,
  handleInsertSavedReply,
  emojiPickerRef,
}: AnimatedTextareaProps): JSX.Element => {
  const comment = useCommentData()
  const parentRef = useRef<HTMLDivElement>(null)

  const handleEmojiSelect = useTextareaEmojiInsert(
    textareaRef,
    replyText,
    setReplyText,
  )

  return (
    <motion.div
      ref={parentRef}
      animate={{ height: isExpanded ? '115px' : '33px' }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={clsx(styles.textareaWrapper, !isExpanded && styles.collapsed)}
      style={{ width: '100%' }}
    >
      <Textarea
        id={id}
        ref={textareaRef}
        name="replyText"
        placeholder={`Reply to @${comment.author.username}`}
        rows={1}
        maxLength={MAX_CHARS}
        value={replyText}
        onChange={(e): void => setReplyText(e.target.value)}
        required
        disabled={isLoading}
        onClick={handleExpand}
        onFocus={handleFocus}
        className={clsx(styles.textarea, error && styles.error)}
        resize="none"
        aria-required
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        data-is-expanded={isExpanded}
      />
      <div
        className={styles.textareaActions}
        data-is-expanded={isExpanded}
        data-is-collapsed={!isExpanded}
      >
        {isExpanded && (
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            emojiPickerRef={emojiPickerRef}
          >
            <IconButton
              label="Select emoji"
              variant="tertiary"
              className={styles.triggerButton}
              tooltip="Select emoji"
            >
              <SmileIcon />
            </IconButton>
          </EmojiPicker>
        )}

        <SavedRepliesPopover
          savedRepliesPopoverRef={savedRepliesPopoverRef}
          handleInsertSavedReply={handleInsertSavedReply}
        />
      </div>
    </motion.div>
  )
}
